import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./DoctorSidebar.scss";
import sidebar1 from "../../Assets/sidebar1.png";
import sidebar2 from "../../Assets/sidebar2.png";
import sidebar3 from "../../Assets/sidebar3.png";
import sidebar4 from "../../Assets/sidebar4.png";
import sidebar5 from "../../Assets/sidebar5.png";
import onofbtn from "../../Assets/on-of-btn.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiMessageCircle } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { tokenManager } from "../../Api/tokenManager";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { getSocket, resetSocketConnection } from "../../Api/socket";
import { showConfirm } from "../../feedback/feedbackApi";
import { resetDoctorSignupState } from "../../Features/Postes/Slice/doctorAuthSlice/doctorAuthSlice";
import { resetDoctorSession } from "../../Features/Postes/Slice/doctorExpertiseSlice";

const getUiRole = () => tokenManager.getActiveProviderRole() === "hospital" ? "hospital" : "doctor";

export default function DoctorSidebar({
  isMobileMenuOpen = false,
  onCloseMobileMenu = () => {},
}) {
  const brandLogo = "/Assets/logo.png";
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [unreadCount, setUnreadCount] = useState(0);
  const prevPathRef = useRef(location.pathname);
  const providerRole = getUiRole();
  const base = providerRole === "hospital" ? "/hospital" : "/doctor";
  const viewedStorageKey =
    providerRole === "hospital"
      ? "healbid_hospital_notifications_viewed_at"
      : "healbid_doctor_notifications_viewed_at";
  const viewedEvent =
    providerRole === "hospital"
      ? "healbid_hospital_notifications_viewed"
      : "healbid_doctor_notifications_viewed";
  const notificationsEndpoint =
    providerRole === "hospital"
      ? ENDPOINTS.HOSPITAL_NOTIFICATIONS
      : ENDPOINTS.DOCTOR_NOTIFICATIONS;
  const notificationsPath = `${base}-notifications`;
  const menuItems = [
    { to: `${base}-bids`, label: "Patient Bids", icon: sidebar1 },
    { to: `${base}-confirmed-patients`, label: "Confirmed Patients", icon: sidebar2 },
    { to: `${base}-appointments`, label: "Appointments", icon: sidebar3 },
    {
      to: `${base}-chats`,
      label: providerRole === "hospital" ? "Hospital Chats" : "Doctor Chats",
      Icon: FiMessageCircle,
    },
    ...(providerRole === "doctor"
      ? [{ to: `${base}-availability`, label: "Manage Availability", icon: sidebar5 }]
      : []),
    { to: `${base}-notifications`, label: "Notifications", icon: sidebar4 },
    { to: `${base}-settings`, label: "Settings", icon: sidebar5 },
  ];

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await API.get(notificationsEndpoint, { params: { page: 1, limit: 100 } });
      const data = Array.isArray(res?.data?.data ?? res?.data) ? (res?.data?.data ?? res?.data) : [];
      let viewedAt = 0;
      try {
        viewedAt = Number(localStorage.getItem(viewedStorageKey)) || 0;
      } catch (_) {}
      const unread = data.reduce((acc, n) => {
        const t = n.updatedAt || n.createdAt;
        if (!t) return acc;
        const ts = new Date(t).getTime();
        if (Number.isNaN(ts)) return acc;
        return ts > viewedAt ? acc + 1 : acc;
      }, 0);
      setUnreadCount(unread);
    } catch (_) {
      setUnreadCount(0);
    }
  }, [notificationsEndpoint, viewedStorageKey]);

  useEffect(() => {
    loadUnreadCount();
    const onViewed = () => setUnreadCount(0);
    window.addEventListener(viewedEvent, onViewed);
    return () => window.removeEventListener(viewedEvent, onViewed);
  }, [loadUnreadCount, viewedEvent]);

  useEffect(() => {
    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        loadUnreadCount();
      }, 10000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: providerRole });
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }

    const onNotification = () => loadUnreadCount();
    const onBidUpdate = () => loadUnreadCount();
    socket.on("notification:new", onNotification);
    socket.on("bid:update", onBidUpdate);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();

    return () => {
      socket.off("notification:new", onNotification);
      socket.off("bid:update", onBidUpdate);
      socket.off("connect", stopPolling);
      socket.off("disconnect", startPolling);
      socket.off("connect_error", startPolling);
      stopPolling();
    };
  }, [loadUnreadCount, providerRole]);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (isMobileMenuOpen) onCloseMobileMenu();
    }
  }, [location.pathname, isMobileMenuOpen, onCloseMobileMenu]);

  const handleLogout = async () => {
    const confirmed = await showConfirm("Are you sure you want to log out?", {
      title: "Log out?",
      confirmText: "Log out",
      cancelText: "Stay",
      danger: true,
    });
    if (!confirmed) return;
    tokenManager.clear();
    resetSocketConnection();
    dispatch(resetDoctorSignupState());
    dispatch(resetDoctorSession());
    navigate("/", { replace: true });
  };

  const badgeText = useMemo(() => {
    if (!unreadCount) return "";
    if (unreadCount > 99) return "99+";
    return String(unreadCount);
  }, [unreadCount]);

  return (
    <div className={`doctor_sid_barr ${isMobileMenuOpen ? "mobile_open" : ""}`}>
      <button
        type="button"
        className="doctor_sidebar_backdrop"
        aria-label="Close doctor menu"
        onClick={onCloseMobileMenu}
      />
      <div className="doctor_sidebar">
        <div className="doctor_logo">
          <img src={brandLogo} alt="HealBid" className="doctor_brand_logo" />
        </div>

        <nav className="doctor_menu" aria-label="Main">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.to;
            const MenuIcon = item.Icon;

            return (
              <Link
                key={item.to}
                to={item.to}
                className={`doctor_menu_item ${isActive ? "active" : ""}`}
              >
                {MenuIcon ? (
                  <span className="doctor_menu_item_icon" aria-hidden>
                    <MenuIcon size={22} strokeWidth={2.2} />
                  </span>
                ) : (
                  <img src={item.icon} alt="" aria-hidden />
                )}
                <span className="doctor_menu_item_label">{item.label}</span>
                {item.to === notificationsPath && badgeText ? (
                  <span className="doctor_menu_item_badge" aria-label={`${badgeText} unread notifications`}>
                    {badgeText}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="doctor_bottom_section">
          <div className="doctor_logout" onClick={handleLogout} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleLogout()}>
            <img src={onofbtn} alt="" />
            <span>Log Out</span>
          </div>
        </div>
      </div>
    </div>
  );
}